import React, { useEffect, useState } from 'react';
import { AccountInfo } from '@/components/AccountInfo/AccountInfo';
import { Button, notification } from 'antd';
import { observer } from 'mobx-react-lite';
import { MessageIcon } from '@/components/MessageIcon/MessageIcon';
import { IEntity } from '@/shared/interfaces/entity.interface';
import { COMMON, MAIN } from '@/configs/main.config';
import declineIcon from '@/public/icons/decline.svg';
import answerIcon from '@/public/icons/answer.svg';
import { State } from '@/services/state.service';
import { useStore } from '@/stores';
import { SolgariApiService } from '@/services/solgari-api/solgari-api.service';
import { ISession, SipJs } from '@/services/sip-js.service';
import { WrapUpApi } from '@/services/solgari-api/interfaces/wrap-up.interface';
import { ESessionStatus } from '@/shared/enums/ringing-status.enum';
import { USER_TYPE } from '@/shared/constants/user';
import { getRegionByPhoneNumber } from '@/services/phoneRegionLocator/getRegionByPhoneNumber';
import './Inbound.scss';

export const Inbound = observer(() => {
  const {
    settingsStore: { notification: settingNotification },
    callStore: { contacts, setWrapUpSession },
  } = useStore();

  const [notificationApi, contextHolder] = notification.useNotification();
  notification.config({ maxCount: 1 });

  const [user, setUser] = useState<IEntity>(COMMON.UNKNOWN_ENTITY);
  const [queueName, setQueueName] = useState<string>(null);
  const [userRegion, setUserRegion] = useState('Unknown');

  const acceptCall = async () => {
    const state = await checkMicrophonePermission();
    if (state === 'granted') {
      const idleSession = SipJs.getSessionByType('idle');
      SipJs.acceptCall(idleSession.id);
    }
  };

  const autoAnswerIfRequired = () => {
    const autoAnswerFlag = localStorage.getItem(MAIN.lsAutoAnswerToggleKey);
    const isAutoAnswerEnabled = autoAnswerFlag
      ? JSON.parse(autoAnswerFlag)
      : false;
    const autoAnswerDelayInSeconds =
      Number(localStorage.getItem(MAIN.lsAutoAnswerTimerKey)) || 0;
    if (isAutoAnswerEnabled) {
      setTimeout(acceptCall, autoAnswerDelayInSeconds * 1000);
    }
  };

  const declineCall = () => {
    const idleSession = SipJs.getSessionByType('idle');
    State.setState({ wrapUpQueue: null });
    SipJs.declineCall(idleSession.id);
  };

  const sendNotification = (entity: IEntity, queueName: string) => {
    const permission = Notification?.permission;

    if (permission !== 'granted' || !settingNotification?.inboundCall) {
      return;
    }

    const title = queueName
      ? `Incoming call via ${queueName}`
      : 'Incoming call';
    const text = `${entity.fullName} (${entity.phone})`;
    const notify = new Notification(title, { body: text, silent: false });
    notify.onclick = () => {
      window.focus();
    };
  };

  const keyListenerInbound = (e: KeyboardEvent) => {
    if (e?.key) {
      switch (e.key) {
        case 'Escape':
          declineCall();
          break;
        case 'Enter':
        case ' ':
          acceptCall();
          break;
        default:
          break;
      }
    }
  };

  const checkMicrophonePermission = async (): Promise<PermissionState> => {
    const { state } = await navigator.permissions.query({
      name: 'microphone' as PermissionName,
    });
    if (state !== 'granted') {
      notificationApi.error({
        message: 'Microphone permission is required',
        placement: 'bottomRight',
        duration: 5,
        className: 'notifications',
      });
    }
    return state;
  };

  useEffect(() => {
    const idleSession = SipJs.getSessionByType('idle');

    let entity: IEntity = COMMON.UNKNOWN_ENTITY;
    let queueName = null;

    if (idleSession) {
      entity = idleSession.remoteUser.connectedUsers[0] as IEntity;
      queueName = idleSession.remoteUser.queueName;
    }

    if (queueName) {
      SolgariApiService.getWrapUpForQueue(queueName).then(
        (value: WrapUpApi) => {
          if (value?.wrapUp) {
            State.setState({
              wrapUpQueue: {
                isWorking: false,
                time: value.wrapUp,
              },
            });
          }
        },
      );
    } else {
      const directWrapUp = Number(
        localStorage.getItem(MAIN.lsWrapUpToggleAllCallsKey),
      );
      if (directWrapUp > 0) {
        State.setState({
          wrapUpQueue: {
            isWorking: false,
            time: directWrapUp,
          },
        });
      }
    }

    if (entity) {
      setUser(entity);
      setQueueName(queueName);
      sendNotification(entity, queueName);
    }

    if (idleSession?.sessionState$) {
      const sub$ = idleSession.sessionState$.subscribe(
        (status: ESessionStatus) => {
          if (status === ESessionStatus.ACCEPTED) {
            setWrapUpSession(idleSession);
          }
        },
      );

      return () => sub$.unsubscribe();
    }
  }, [contacts]);

  useEffect(() => {
    async function checkInboundCallAndPermissions() {
      const state = await checkMicrophonePermission();
      if (state === 'granted') {
        document.addEventListener('keydown', keyListenerInbound);
        autoAnswerIfRequired();
      }
    }

    checkInboundCallAndPermissions();

    return () => {
      document.removeEventListener('keydown', keyListenerInbound);
    };
  }, []);

  useEffect(() => {
    if (
      user?.initialPhone &&
      user?.initialPhone !== COMMON.UNKNOWN_ENTITY.phone
    ) {
      setUserRegion(getRegionByPhoneNumber(user.initialPhone));
    }
  }, [user]);

  const idleSession: ISession = SipJs.getSessionByType('idle');

  return (
    <div className="inbound">
      {contextHolder}
      <div className="call-info">
        <div className="main-text">Inbound Call</div>
        {queueName ? (
          <div className="main-text">{`via ${queueName}`}</div>
        ) : (
          idleSession?.remoteUser?.type === USER_TYPE.EXTERNAL && (
            <div className="back-text">{userRegion}</div>
          )
        )}
      </div>
      <AccountInfo
        entity={user.type}
        id={user.id}
        fullName={user.fullName}
        phone={user.initialPhone}
        searchIsActive={!contacts}
      />
      <div className="call-controls">
        <Button
          danger
          data-testid="decline-call"
          className="call-button"
          type="primary"
          shape="circle"
          onClick={declineCall}
          icon={<MessageIcon svgUrl={declineIcon} />}
        />
        <Button
          data-testid="accept-call"
          className="call-button answer"
          type="primary"
          shape="circle"
          onClick={acceptCall}
          icon={<MessageIcon svgUrl={answerIcon} />}
        />
      </div>
    </div>
  );
});
